import ContactPersonItem from "../Entities/ContactPersonItem";

export { ImportGeneric as default };

/**
* Generic Parse
*
* Handler all data from file import contacts
*
* @author      brunorbarroso <bruno-b-rodrigues@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ImportGeneric {
  
  /**
   * Regex to validate file
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  regex = null;

  /**
   * Contacts from file
   *
   * @public
   *
   * @type {Object|null}
   *
   * @since 7.0.0
   */
  content = null;

  /**
   * Contacts Config Parse from CSV type
   *
   * @public
   *
   * @type {Object}
   *
   * @since 7.0.0
   */
  fields = {}

  /**
   * Constructor 
   * 
   * @param {String} content 
   */
  constructor(content) {
    this.setContent(content);
  }

  /**
   * Method to save contacts correctly 
   * 
   * @param {String} content
   * @return {void} 
   */
  setContent(content) {
    if (!this.isContentValid(content)){
      throw new Error('Content not valid.')
    }

    this.content = content;
  }

  /**
   * Method to check if is content valid or not
   * 
   * @param {string} string 
   * @returns {boolean}
   */
  isContentValid(string) {
    return this.regex ? this.regex.test(string) : true;
  }

  /**
   * Method to apply the parse and return values
   * 
   * @returns {Object} list mapped
   */
  toSapoObject(){
    return this.content.map(contact => {
      const person = new ContactPersonItem;
      person.email  = this.getValueByField(contact, this.fields.email);
      person.mobile_phone = this.getValueByField(contact, this.fields.cellPhone);
      person.name = this.getValueByField(contact, this.fields.lastname) || person.email || person.mobile_phone;
      person.id = this.createHashCode(person.name);
      person.company = this.getValueByField(contact, this.fields.company);
      person.birth_date = this.normalizeDate(this.getValueByField(contact, this.fields.birthday));
      person.home_address = this.getValueByField(contact, this.fields.homeStreet);
      person.notes = this.getValueByField(contact, this.fields.notes);
      return person;
    })
  }

  /**
   * Create a hash from a string
   *
   * @param {String} value
   * @returns {String}
   */
  createHashCode(value){
    return btoa(encodeURIComponent(value));
  }

  /**
   * Converts a date to the YYYY-MM-DD format, accepting year first (Y-M-D)
   * or day first (D/M/Y) dates with any of the - / . or space separators
   *
   * @param {String} value
   * @returns {String} formatted date, or an empty string when it isn't valid
   */
  normalizeDate(value){
    const date = value.trim();
    let year, month, day, parts;

    if ((parts = date.match(/^(\d{4})[-/.\s]+(\d{1,2})[-/.\s]+(\d{1,2})(T.*)?$/) || date.match(/^(\d{4})(\d{2})(\d{2})$/))) {
      [, year, month, day] = parts;
    } else if ((parts = date.match(/^(\d{1,2})[-/.\s]+(\d{1,2})[-/.\s]+(\d{4})$/))) {
      [, day, month, year] = parts;
    } else {
      return '';
    }

    // Rejects dates that don't exist, like month 30 or February 31
    const parsed = new Date(Date.UTC(year, month - 1, day));
    if (parsed.getUTCFullYear() !== +year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== +day) {
      return '';
    }

    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  /**
   * Adds a value to the content array from a single key.
   *
   * @param {Object} item -Contact object.
   * @param {String} key -Unique key to look for in the object.
   * @param {Array} content -Array of content where the value will be added.
   */
  addValueFromKey(item, key, content, field) {
    const value = this.getValueByKey(item, key, field);
    if (value && !content.includes(value)) {
      content.push(value);
    }
  }

  /**
   * Adds values ​​to the content array from a set of keys.
   *
   * @param {Object} item - Contact object.
   * @param {Array} keys -Set of keys to look for in the object.
   * @param {Array} content -Array of content where values ​​will be added.
   */
  addValuesFromKeys(item, keys, content, field) {
    let key_uniq = this.getKeysUnique(keys);
    let values   = [];

    // Parts are joined as a single value, so repeated parts (like day 4 of month 4) are kept
    for (const sub_key of key_uniq) {
      const value = this.getValueByKey(item, sub_key, field).trim();
      if (value) {
        values.push(value);
      }
    }

    const value = values.length ? ` ${values.join(' ')}` : '';
    if (value && !content.includes(value)) {
      content.push(value);
    }
  }

  /**
   * Gets the unique values ​​of an object based on a set of keys.
   * 
   * @param {Object} item contact object
   * @param {Object} field list of keys to search in object
   * 
   * @returns {String} content from object
   */
  getValueByField(item, field) {
    let content = [];

    const keys_uniq = this.getKeysUnique(field.keys);

    for (const key of keys_uniq) {
      if(!(key instanceof Array)) {
        this.addValueFromKey(item, key, content, field);
      }else{
        this.addValuesFromKeys(item, key, content, field);
      }

      if (field.priority && content.length > 0) {
        break;
      }
    }

    return content.join(' ').trim().toString();
  }

  /**
   * Returns an array with unique keys
   * @param {Array} array
   * @returns {Array}
   */
  getKeysUnique(array){
    return [...new Set(array)];
  }

  /**
   * 
   * @param {Object} item contact object
   * @param {Array} keys list of keys to search in object
   * @returns {String} content from object
   */
  getValueByKey(item, key){
    return item[key] ? ` ${item[key]}` : '';
  }
}